import { useCallback, useEffect, useRef } from "react";
import { App, Flex } from "antd";
import type { AnyObject } from "antd/es/_util/type";
import { ColumnsType } from "antd/es/table";
import { useTranslation } from "react-i18next";
import errorCodes from "@/apis/error";
import { myBorrowed } from "@/apis/equipment";
import useDataList from "@/hooks/useDataList";
import STableLayout, {
  DEFAULT_PAGE_SIZE,
  TableLayoutRef,
} from "@/styles/STableLayout";
import { Equipment } from "@/types/equipment";
import { useEquipmentEnumsContext } from "../../context/EquipmentEnumsContext";
import useMyBorrowedColumn from "../../hooks/useMyBorrowedColumn";

// 我的领用设备列表
const MyBorrowed = () => {
  const { t } = useTranslation();
  const { notification } = App.useApp();
  const tableRef = useRef<TableLayoutRef | null>(null); // 表格分页信息
  const controllerRef = useRef<AbortController>(); // 当前列表请求控制器
  const [items, total, loading, { fetchList }] = useDataList<
    Equipment,
    Parameters<typeof myBorrowed>
  >({ fetchApi: myBorrowed });
  const { statusItems } = useEquipmentEnumsContext(); // 设备状态枚举

  // 加载指定页的领用设备
  const loadList = useCallback(async (offset: number) => {
    // 发起新请求前取消旧请求，避免过期数据覆盖当前列表
    controllerRef.current?.abort();
    const controller = new AbortController();
    controllerRef.current = controller;

    try {
      await fetchList({
        pagination: {
          offset,
          take: tableRef.current?.pageSize ?? DEFAULT_PAGE_SIZE,
        },
      }, controller);
    } catch (error) {
      // 主动取消请求时不显示错误提示
      if (!controller.signal.aborted) {
        notification.error({
          message: t("equipment.myBorrowedFetchFailed"),
          description: errorCodes[Number(error)] ?? String(error),
        });
      }
    }
  }, [fetchList, notification, t]);

  // 首次进入加载第一页，离开页面时取消未完成的请求
  useEffect(() => {
    loadList(0);
    return () => controllerRef.current?.abort();
  }, [loadList]);

  // 根据共享状态枚举生成表格列
  const columns = useMyBorrowedColumn({ statusItems });

  return (
    <Flex vertical style={{ width: "100%", height: "100%" }}>
      <STableLayout
        rowKey="id"
        loading={loading}
        dataSource={items}
        columns={columns as ColumnsType<AnyObject>}
        headerChildren={null}
        tableLayoutRef={tableRef}
        total={total}
        scrollX={2480}
        handleChange={loadList}
      />
    </Flex>
  );
};

export default MyBorrowed;
